/**
 * Camada de leitura da paleta e da tipografia reais do projeto.
 *
 * As cores NÃO são duplicadas manualmente aqui: os valores são lidos das
 * variáveis CSS declaradas em `src/index.css`. Se uma cor do site mudar no
 * futuro, a Ficha do site passa a exibir automaticamente o novo valor.
 */

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface PaletteSwatch {
  step: number;
  token: string;
  hex: string;
  rgb: string;
  oklch: string;
}

export interface PaletteApplication {
  usage: string;
  token: string;
}

export interface PaletteFamily {
  key: string;
  label: string;
  description: string;
  mainApplication: string;
  anchors: number[];
  applications: PaletteApplication[];
}

export interface TypographyRole {
  role: string;
  family: string;
  variable: string;
  weights: string;
  usage: string;
}

export const SCALE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

export const paletteFamilies: PaletteFamily[] = [
  {
    key: "background",
    label: "Background",
    description: "branco sujo e bege claro",
    mainApplication: "Fundo geral do site, seções, cartões e painéis.",
    anchors: [50],
    applications: [
      { usage: "fundo geral", token: "background-50" },
      { usage: "fundo secundário", token: "background-100" },
      { usage: "cartões e divisórias", token: "background-200" },
      { usage: "bordas", token: "background-300" },
    ],
  },
  {
    key: "foreground",
    label: "Foreground",
    description: "marrom café",
    mainApplication: "Textos, títulos e contraste de leitura.",
    anchors: [950],
    applications: [
      { usage: "títulos", token: "foreground-900" },
      { usage: "textos", token: "foreground-700" },
      { usage: "textos secundários", token: "foreground-500" },
      { usage: "ícones de apoio", token: "foreground-400" },
    ],
  },
  {
    key: "primary",
    label: "Primary",
    description: "vinho e ameixa",
    mainApplication: "Ações principais, botões e marca.",
    anchors: [500],
    applications: [
      { usage: "botões", token: "primary-500 / primary-600" },
      { usage: "marca e destaques", token: "primary-700" },
      { usage: "fundos suaves", token: "primary-50 / primary-100" },
      { usage: "rodapé", token: "primary-900" },
    ],
  },
  {
    key: "accent",
    label: "Accent",
    description: "rosa queimada",
    mainApplication: "Destaques, etiquetas e avisos.",
    anchors: [500],
    applications: [
      { usage: "destaques e etiquetas", token: "accent-500" },
      { usage: "avisos", token: "accent-100 / accent-200" },
      { usage: "favoritos", token: "accent-500" },
      { usage: "texto de alerta", token: "accent-800" },
    ],
  },
  {
    key: "secondary",
    label: "Secondary",
    description: "dourado fosco",
    mainApplication: "Apoio, filtros, molduras e detalhes.",
    anchors: [500],
    applications: [
      { usage: "detalhes e molduras", token: "secondary-300 / secondary-400" },
      { usage: "selos neutros", token: "secondary-100 / secondary-200" },
      { usage: "faixas suaves", token: "secondary-50" },
      { usage: "avisos", token: "secondary-700" },
    ],
  },
];

export const typographyRoles: TypographyRole[] = [
  {
    role: "Fonte dos títulos",
    family: "Cormorant Garamond",
    variable: "--font-heading",
    weights: "400 · 500 · 600 · 700 (inclui itálico)",
    usage: "Títulos, cabeçalhos, nome da marca e números de destaque.",
  },
  {
    role: "Fonte dos textos",
    family: "Jost",
    variable: "--font-body",
    weights: "300 · 400 · 500 · 600",
    usage: "Textos corridos, descrições, botões, rótulos e formulários.",
  },
];

/* ------------------------------------------------------------------ */
/* Conversão OKLCH → sRGB → HEX                                         */
/* ------------------------------------------------------------------ */

const oklchToRgb = (L: number, C: number, H: number): RGB => {
  const hRad = (H * Math.PI) / 180;
  const a = C * Math.cos(hRad);
  const b = C * Math.sin(hRad);

  const lPrime = L + 0.3963377774 * a + 0.2158037573 * b;
  const mPrime = L - 0.1055613458 * a - 0.0638541728 * b;
  const sPrime = L - 0.0894841775 * a - 1.291485548 * b;

  const l = lPrime ** 3;
  const m = mPrime ** 3;
  const s = sPrime ** 3;

  const rLin = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const gLin = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const bLin = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

  const encode = (value: number): number => {
    const clamped = Math.min(1, Math.max(0, value));
    const encoded =
      clamped <= 0.0031308 ? clamped * 12.92 : 1.055 * clamped ** (1 / 2.4) - 0.055;
    return Math.round(Math.min(1, Math.max(0, encoded)) * 255);
  };

  return { r: encode(rLin), g: encode(gLin), b: encode(bLin) };
};

const toHex = ({ r, g, b }: RGB): string =>
  `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, "0")).join("")}`.toUpperCase();

/* ------------------------------------------------------------------ */
/* Leitura das variáveis CSS                                            */
/* ------------------------------------------------------------------ */

export const readPaletteFamily = (family: string): PaletteSwatch[] => {
  if (typeof window === "undefined") return [];
  const styles = getComputedStyle(document.documentElement);

  return SCALE_STEPS.flatMap((step) => {
    const raw = styles.getPropertyValue(`--${family}-${step}`).trim();
    if (!raw) return [];
    const [L, C, H] = raw.split(/\s+/).map(Number);
    if (!Number.isFinite(L)) return [];

    const channelC = Number.isFinite(C) ? C : 0;
    const channelH = Number.isFinite(H) ? H : 0;
    const rgb = oklchToRgb(L, channelC, channelH);

    return [
      {
        step,
        token: `${family}-${step}`,
        hex: toHex(rgb),
        rgb: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
        oklch: `oklch(${L} ${channelC} ${channelH})`,
      },
    ];
  });
};

export const readFontFamily = (variable: string): string => {
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
};